import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import * as angular from 'angular';
import { Store } from '@ngrx/store';
import { downgradeComponent } from '@angular/upgrade/static';
import { updateComponent } from '../../../store/component.actions';
import { AppState } from '../../../../store';
import { DesignerSchemaFieldI } from '../../../models/designer-schema-field.model';
import { CollectionSettings } from '../fields-collection.models';
import { Schema } from '../../../package.models';

function autoGenerateAlias(name: string): string {
  if (name) {
    const nonDashAlphanumerics = /[^a-zA-Z\d!_]/g;
    const hasNonDashAlphanumerics =
      name.match(nonDashAlphanumerics) !== null && name.match(nonDashAlphanumerics).length > 0;

    // replace all non-alphanumeric characters with underscore
    let alias = name.replace(/[^a-zA-Z\d]/g, '_');

    if (hasNonDashAlphanumerics) {
      // replace consecutive instances of underscores with a single underscore
      alias = alias.replace(/_{2,}/g, '_');
    }
    // take the first 50 characters and trim them from beginning or trailing underscores
    alias = alias.replace(/^_+|_+$/g, '');

    return alias;
  }
  return '';
}

@Component({
  selector: 'select-collection',
  template: `
    <xp-fields-collection
      [records]="recordsCopy"
      [collectionSettings]="collectionSettings"
      [isValid]="valid"
      (validityChange)="onFieldsValidityChange($event)"
      (save)="save($event)"
      (recordsChange)="onRecordChange($event)"
      [columns]="['expression', 'alias']"
      duplicationValidationProp="projected_name"
      duplicationValidationPropName="Alias"
    >
      <ng-template templateName="expression" let-item>
        <xp-field-expression-editor
          [value]="item.record.name"
          [schema]="parentSchemas[0]"
          [fields]="(parentSchemas[0] || {}).fields || []"
          [index]="item.index"
          [focusedProp]="item.focusedProp"
          class="fields-collection-editor"
          (fieldChange)="onFieldChange($event, item.record, 'name')"
        ></xp-field-expression-editor>
      </ng-template>
      <ng-template templateName="expression-header" let-item>
        {{ 'fields-collection.headers.field-expression-editor' | translate }}
      </ng-template>

      <ng-template templateName="alias" let-item>
        <xp-alias-editor
          [value]="item.record.projected_name"
          [index]="item.index"
          propName="projected_name"
          [focusedProp]="item.focusedProp"
          [isDuplicateError]="item.record.isDuplicateError"
          class="fields-collection-editor"
          (fieldChange)="onFieldChange($event, item.record, 'projected_name')"
        ></xp-alias-editor>
      </ng-template>
      <ng-template templateName="alias-header" let-item>
        <xp-alias-editor-title [title]="'fields-collection.headers.alias-editor' | translate"></xp-alias-editor-title>
      </ng-template>
    </xp-fields-collection>
  `,
  providers: [],
})
export class SelectCollectionComponent implements OnInit {
  @Input() records: DesignerSchemaFieldI[];
  @Input() valid: boolean;
  @Input() active: boolean;
  @Input() collectionSettings: CollectionSettings;
  @Input() parentSchemas: Schema[];
  @Output() recordsChange = new EventEmitter();
  @Output() validityChange = new EventEmitter();

  recordsCopy: DesignerSchemaFieldI[] = [];

  constructor(private store: Store<AppState>) {}

  ngOnInit() {
    this.recordsCopy = [...this.records].map((item) => ({ ...item, id: uuidv4() }));

    const defaultCollectionSettings: CollectionSettings = {
      itemsPerPage: 20,
      emptyRecord: { name: '', projected_name: '', FC_pristine: true },
      parentSchemas: this.parentSchemas,
      autoGenerateFn: this.autoGenerateAliasAndUpdateRecords.bind(this),
      autoFillFns: [
        {
          text: 'Auto-fill',
          func: this.autoFill.bind(this),
        },
      ],
    };

    this.collectionSettings = { ...(this.collectionSettings || {}), ...defaultCollectionSettings };
  }

  autoFill(): DesignerSchemaFieldI[] {
    if (!this.parentSchemas[0]) {
      return [this.collectionSettings.emptyRecord];
    }

    if (this.parentSchemas && this.parentSchemas[0] && this.parentSchemas[0].fields) this.recordsCopy = [];

    this.recordsCopy = this.parentSchemas[0].fields.map((field) => ({
      name: field.name,
      projected_name: autoGenerateAlias(field.name),
      id: uuidv4(),
    }));

    this.onRecordChange({ records: this.recordsCopy });
    return this.recordsCopy;
  }

  autoGenerateAliasAndUpdateRecords(record: DesignerSchemaFieldI) {
    const alias = autoGenerateAlias(record.name);

    if (alias) {
      this.recordsCopy = this.recordsCopy.map((item) => {
        if (item.id === record.id) {
          return {
            ...item,
            projected_name: alias,
            alias,
          };
        }
        return item;
      });
    }

    this.onRecordChange({ records: this.recordsCopy });
  }

  save(records: DesignerSchemaFieldI[]) {
    this.records = records.map((record) => {
      return { name: record.name, projected_name: record.projected_name };
    });
  }

  onRecordChange({ records }) {
    this.recordsCopy = records;
    const fields = records.map(({ name, projected_name }) => ({ name, projected_name }));
    this.store.dispatch(updateComponent({ component: { fields } }));
    this.recordsChange.emit(fields);
  }

  onFieldChange(value: string, record: DesignerSchemaFieldI, prop: keyof DesignerSchemaFieldI) {
    this.recordsCopy = this.recordsCopy.map((item) => {
      if (item.id === record.id) {
        return {
          ...item,
          [prop]: value,
        };
      }
      return item;
    });

    this.onRecordChange({
      records: this.recordsCopy,
    });
  }

  onFieldsValidityChange(value: boolean) {
    this.validityChange.emit(value);
  }
}

((window as any).xplenty as angular.IModule).directive(
  'selectCollection',
  downgradeComponent({
    component: SelectCollectionComponent,
  }),
);
